import React, { useEffect } from "react";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import NextLink from "next/link";
import { Controller, useForm } from "react-hook-form";
import { useRouter } from "next/router";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ROUTE_LOGIN,
  ROUTE_PRIVACY,
  ROUTE_REGISTER_COMPLETE,
  ROUTE_TNC,
} from "config/routes";
import { RegisterForm, RegisterFormGrid } from "./styles";
import PasswordField from "components/CustomTextField/password";
import CustomAutocomplete from "components/CustomAutocomplete";
import TextField from "components/CustomTextField";
import postRegisterSchema from "validations/postRegisterSchema";
import countries from "config/available-countries";
import { useIntl } from "react-intl";
import { REGISTER_SOURCE } from "config/app";
import { URL_REGISTER } from "config/endpoints";
import { IconArrowNarrowRight } from "@tabler/icons";
import useIP from "services/auth/get-ip-country";

import { track } from "hooks/useAnalytics";
import { TRACK_USER_REGISTER, TRACK_VIEW_REGISTER } from "config/tracking";
import getErrorMessage from "./getErrorMessage";
import createLead from "services/lead/create-lead";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { postFetcher } from "utils/fetcher";
import { useLocale } from "hooks/useLocale";
import languages from "components/LanguageSelect/languages";
import { styled } from "@mui/material/styles";

const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    margin: "auto",
  },
  marginTop: "8px",
  marginBottom: "8px",
}));
export default function Form() {
  const router = useRouter();
  const intl = useIntl();
  const { setLocale, getCookieLocale } = useLocale();
  const locale = getCookieLocale();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { utm_source, lead, ...others } = router.query;
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(postRegisterSchema(intl)),
    shouldUseNativeValidation: false,
    defaultValues: {
      firstName: "",
      lastName: "",
      companyName: "",
      country: null,
      email: "",
      password: "",
      ip: null,
      time_zone: "" + (0 - new Date().getTimezoneOffset() * 60),
      source: utm_source || REGISTER_SOURCE,
    },
  });

  const { data: ip } = useIP();
  const country = watch("country");
  useEffect(() => setValue("source", utm_source), [utm_source]);
  useEffect(() => setValue("email", lead), [lead]);

  useEffect(() => {
    track(TRACK_VIEW_REGISTER, {
      source: utm_source || REGISTER_SOURCE,
    });
  }, []);

  useEffect(() => {
    if (ip) {
      setValue("ip", ip);
      setValue("country", ip.country);
    }
  }, [ip]);

  useEffect(() => {
    if (lead) {
      createLead({ utm_source, email: lead, ...others });
    }
  }, [lead]);

  useEffect(() => {
    if (!!locale) {
      setLocale(locale);
    } else {
      const langCode = languages(intl).find((t) =>
        t.countries?.some((c) => c === country)
      )?.code;

      if (!!langCode) {
        setLocale(langCode);
      }
    }
  }, [country]);

  const onSubmit = async (data) => {
    try {
      const token = await executeRecaptcha("registerEmployer");
      const payload = {
        ...data,
        ip: { ...data?.ip, city: null, region: null, postal: null },
      };
      await postFetcher(URL_REGISTER, { token, ...payload });
      const trackData = { ...data, password: "" };
      await track(TRACK_USER_REGISTER, {
        ...trackData,
        registrationCountry: data.country,
      });
      if (typeof window?.lintrk === "function") {
        window.lintrk("track", { conversion_id: 7857372 });
      }
      router.push({
        pathname: ROUTE_REGISTER_COMPLETE,
        query: { user: data.email },
      });
    } catch (e) {
      setError("form", {
        type: "manual",
        message: getErrorMessage(intl, e.info?.err_msg),
      });
    }
  };

  return (
    <RegisterForm component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
      <RegisterFormGrid container>
        <Grid item sm={12} md={6} xs={12}>
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                label={intl.formatMessage({
                  id: "First Name",
                  defaultMessage: "First Name",
                })}
                type="text"
                placeholder={intl.formatMessage({
                  id: "First Name",
                  defaultMessage: "First Name",
                })}
                error={error}
                {...field}
                required
              />
            )}
          />
        </Grid>
        <Grid item sm={12} md={6} xs={12}>
          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                label={intl.formatMessage({
                  id: "Last Name",
                  defaultMessage: "Last Name",
                })}
                type="text"
                placeholder={intl.formatMessage({
                  id: "Last Name",
                  defaultMessage: "Last Name",
                })}
                error={error}
                {...field}
                required
              />
            )}
          />
        </Grid>
        <Grid item sm={12} md={6} xs={12}>
          <Controller
            control={control}
            name="companyName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                label={intl.formatMessage({
                  id: "Company Name",
                  defaultMessage: "Company Name",
                })}
                type="text"
                placeholder={intl.formatMessage({
                  id: "Company Name",
                  defaultMessage: "Company Name",
                })}
                error={error}
                {...field}
                required
              />
            )}
          />
        </Grid>
        <Grid item sm={12} md={6} xs={12}>
          <Controller
            control={control}
            name="country"
            render={({ field, fieldState: { error } }) => (
              <CustomAutocomplete
                {...field}
                label={intl.formatMessage({
                  id: "Looking for Candidates in?",
                  defaultMessage: "Looking for Candidates in?",
                })}
                placeholder={intl.formatMessage({
                  id: "Select Country",
                  defaultMessage: "Select Country",
                })}
                options={countries}
                optionLabel="name"
                optionValue="code"
                optionGroupBy="region"
                error={error}
                multiple={false}
                required
                autoHighlight
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={intl.formatMessage({
                  id: "Email Address",
                  defaultMessage: "Email Address",
                })}
                type="email"
                placeholder={intl.formatMessage({
                  id: "Email Address",
                  defaultMessage: "Email Address",
                })}
                error={error}
                autoComplete="off"
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="password"
            render={({ field, fieldState: { error } }) => (
              <PasswordField
                {...field}
                label={intl.formatMessage({
                  id: "Password",
                  defaultMessage: "Password",
                })}
                placeholder={intl.formatMessage({
                  id: "Min 8 alphanumeric characters",
                  defaultMessage: "Min 8 alphanumeric characters",
                })}
                error={error}
                autoComplete="off"
                required
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          {errors.form && (
            <Box>
              <FormHelperText error>{errors.form.message}</FormHelperText>
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{
              textAlign: "center",
            }}
          >
            {intl.formatMessage(
              {
                id: "Already have an account? {login_link}",
                defaultMessage: "Already have an account? {login_link}",
              },
              {
                login_link: (
                  <NextLink href={ROUTE_LOGIN} passHref>
                    <Link underline="none" color={"secondary"}>
                      {intl.formatMessage({
                        id: "Log In",
                        defaultMessage: "Log In",
                      })}
                    </Link>
                  </NextLink>
                ),
              }
            )}
          </Typography>
        </Grid>
        <Grid item md={12} xs={12}>
          <StyledButton
            type="submit"
            variant="contained"
            sx={{ mt: 1, mb: 1 }}
            endIcon={<IconArrowNarrowRight />}
            disabled={isSubmitting}
          >
            {intl.formatMessage({
              id: "Get Started for Free",
              defaultMessage: "Get Started for Free",
            })}
          </StyledButton>
        </Grid>
        <Grid item md={12}>
          <Typography variant="subtitle2" color="text.secondary" component="p">
            {intl.formatMessage({
              id: "No credit card required. Cancel anytime.",
              defaultMessage: "No credit card required. Cancel anytime.",
            })}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" component="p">
            {intl.formatMessage(
              {
                id: "By signing up, you agree to the {terms_link} & {privacy_link}",
                defaultMessage:
                  "By signing up, you agree to the {terms_link} & {privacy_link}",
              },
              {
                terms_link: (
                  <Link href={ROUTE_TNC} color="secondary" target="_blank">
                    {intl.formatMessage({
                      id: "Terms of Use",
                      defaultMessage: "Terms of Use",
                    })}
                  </Link>
                ),
                privacy_link: (
                  <Link href={ROUTE_PRIVACY} color="secondary" target="_blank">
                    {intl.formatMessage({
                      id: "Privacy Policy",
                      defaultMessage: "Privacy Policy",
                    })}
                  </Link>
                ),
              }
            )}
          </Typography>
          <Controller
            name="source"
            control={control}
            render={({ field }) => <input type="hidden" {...field} />}
          />
          <Controller
            name="time_zone"
            control={control}
            render={({ field }) => <input type="hidden" {...field} />}
          />
        </Grid>
      </RegisterFormGrid>
    </RegisterForm>
  );
}
